import {
  startOfMonth, endOfMonth,
  startOfQuarter, endOfQuarter,
  startOfYear, endOfYear,
  subMonths, subQuarters, subYears, subDays,
  format, differenceInDays,
} from 'date-fns';

export type DateFilterKey =
  | 'this-month' | 'last-month'
  | 'this-quarter' | 'last-quarter'
  | 'last-12-months' | 'this-year' | 'last-year'
  | 'last-30-days' | 'all-time' | 'custom';

export interface DateFilterOption {
  key: DateFilterKey;
  label: string;
}

export const DATE_FILTER_OPTIONS: DateFilterOption[] = [
  { key: 'last-30-days',   label: 'Last 30 Days'    },
  { key: 'this-month',     label: 'This Month'      },
  { key: 'last-month',     label: 'Last Month'      },
  { key: 'this-quarter',   label: 'This Quarter'    },
  { key: 'last-quarter',   label: 'Last Quarter'    },
  { key: 'this-year',      label: 'This Year'       },
  { key: 'last-year',      label: 'Last Year'       },
  { key: 'last-12-months', label: 'Last 12 Months'  },
  { key: 'all-time',       label: 'All Time'        },
  { key: 'custom',         label: 'Custom Range'    },
];

const fmt = (d: Date): string => format(d, 'yyyy-MM-dd');

export function dateFilterToRange(
  key: Exclude<DateFilterKey, 'custom' | 'all-time'>,
): { startDate: string; endDate: string } {
  const now = new Date();
  switch (key) {
    case 'this-month':
      return { startDate: fmt(startOfMonth(now)), endDate: fmt(now) };
    case 'last-month': {
      const prev = subMonths(now, 1);
      return { startDate: fmt(startOfMonth(prev)), endDate: fmt(endOfMonth(prev)) };
    }
    case 'this-quarter':
      return { startDate: fmt(startOfQuarter(now)), endDate: fmt(now) };
    case 'last-quarter': {
      const prev = subQuarters(now, 1);
      return { startDate: fmt(startOfQuarter(prev)), endDate: fmt(endOfQuarter(prev)) };
    }
    case 'last-12-months':
      return { startDate: fmt(subMonths(now, 12)), endDate: fmt(now) };
    case 'this-year':
      return { startDate: fmt(startOfYear(now)), endDate: fmt(now) };
    case 'last-year': {
      const prev = subYears(now, 1);
      return { startDate: fmt(startOfYear(prev)), endDate: fmt(endOfYear(prev)) };
    }
    case 'last-30-days':
      return { startDate: fmt(subDays(now, 30)), endDate: fmt(now) };
  }
}

/** True when the date range spans ≤90 days (use daily granularity). */
export function isShortDateRange(startDate: string, endDate: string): boolean {
  try {
    return differenceInDays(new Date(endDate), new Date(startDate)) <= 90;
  } catch {
    return true;
  }
}

/** Resolve a DateFilterKey + optional custom dates to { startDate, endDate } strings. */
export function resolveDateFilter(
  key: DateFilterKey | '',
  customFrom?: Date,
  customTo?: Date,
): { startDate: string; endDate: string } | undefined {
  if (!key) return undefined;
  if (key === 'all-time') return { startDate: '2000-01-01', endDate: fmt(new Date()) };
  if (key === 'custom') {
    if (!customFrom || !customTo) return undefined;
    return { startDate: fmt(customFrom), endDate: fmt(customTo) };
  }
  return dateFilterToRange(key);
}
